import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { Box, Chip, Typography } from "@mui/material";
import { JobDetailAssignment, JobDetailPickupCompletion, JobDetailViewModel } from "./jobDetailTypes";
import { DetailCard } from "./DetailCard";

type Props = {
  job: JobDetailViewModel;
};

function DeltaChip({ delta }: { delta: number }) {
  if (delta === 0) {
    return (
      <Chip
        label="Matches estimate"
        size="small"
        sx={{
          height: 22,
          fontSize: 11,
          fontWeight: 600,
          backgroundColor: "#ECEFF1",
          color: "#546E7A",
        }}
      />
    );
  }

  const isUp = delta > 0;
  return (
    <Chip
      label={`${isUp ? "+" : ""}${delta} vs. client estimate`}
      size="small"
      sx={{
        height: 22,
        fontSize: 11,
        fontWeight: 600,
        backgroundColor: isUp ? "#E8F5E9" : "#FFEBEE",
        color: isUp ? "#2E7D32" : "#C62828",
      }}
    />
  );
}

export default function PickupCompletedCard({ job }: Props) {
  const assignment = job.currentAssignment as JobDetailAssignment;
  const completion = job.pickupCompletion as JobDetailPickupCompletion;

  return (
    <DetailCard title="Pickup Completed">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          backgroundColor: "#E3F2FD",
          borderRadius: "8px",
          px: 2,
          py: 1.25,
          mb: 2.5,
        }}
      >
        <LocalShippingIcon sx={{ color: "#1565C0", fontSize: 20 }} />
        <Typography fontWeight={700} sx={{ fontSize: 13, letterSpacing: 0.4, color: "#0D47A1" }}>
          {completion.pickedUpOnLabel}
        </Typography>
      </Box>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 1.5, fontWeight: 700, letterSpacing: 0.8 }}
      >
        COUNT SHEET – VERIFIED ON-SITE
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
          gap: 1.5,
          mb: 1.5,
        }}
      >
        {completion.countSheetItems.map((item) => (
          <Box
            key={item.label}
            sx={{
              border: "1px solid #ECECEC",
              borderRadius: "10px",
              p: 2,
              backgroundColor: "background.paper",
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 700, letterSpacing: 0.8, display: "block", mb: 0.5 }}
            >
              {item.label}
            </Typography>
            <Typography fontWeight={700} sx={{ fontSize: 32, lineHeight: 1.1, mb: 1 }}>
              {item.verifiedCount}
            </Typography>
            <DeltaChip delta={item.delta} />
          </Box>
        ))}
      </Box>

      {completion.submittedLabel && (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12, mb: 2.5 }}>
          {completion.submittedLabel}
        </Typography>
      )}

      <Box sx={{ borderTop: "1px solid #ECECEC", pt: 2 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1.5, fontWeight: 700, letterSpacing: 0.8 }}
        >
          VENDOR
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "8px",
              backgroundColor: assignment.color,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: 16,
              flexShrink: 0,
            }}
          >
            {assignment.initial}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 0.25 }}>
              <Typography fontWeight={700} sx={{ fontSize: 16 }}>
                {assignment.vendorName}
              </Typography>
              <Chip
                label="PICKED UP"
                size="small"
                sx={{
                  height: 20,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  backgroundColor: "#E8F5E9",
                  color: "#2E7D32",
                }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
              {assignment.vendorType}
            </Typography>
          </Box>
          <Typography fontWeight={700} sx={{ fontSize: 15, flexShrink: 0 }}>
            {completion.finalPriceLabel}
          </Typography>
        </Box>
      </Box>
    </DetailCard>
  );
}
